import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { logger } from '../utils/logger';

type DataType = 'contacts' | 'leads' | 'companies' | 'meetings' | 'activities' | 'users';
type DataOperation = 'create' | 'update' | 'delete';

interface DataSyncEvent {
  type: DataType;
  operation: DataOperation;
  id?: string;
  timestamp: number;
}

interface DataSyncContextType {
  notifyDataChange: (type: DataType, operation: DataOperation, id?: string) => void;
  subscribe: (type: DataType, callback: (event: DataSyncEvent) => void) => () => void;
  subscribeAll: (callback: (event: DataSyncEvent) => void) => () => void;
}

const DataSyncContext = createContext<DataSyncContextType | undefined>(undefined);

export function DataSyncProvider({ children }: { children: React.ReactNode }) {
  const listenersRef = useRef<Map<DataType, Set<(event: DataSyncEvent) => void>>>(new Map());
  const globalListenersRef = useRef<Set<(event: DataSyncEvent) => void>>(new Set());

  const notifyDataChange = useCallback((type: DataType, operation: DataOperation, id?: string) => {
    const event: DataSyncEvent = {
      type,
      operation,
      id,
      timestamp: Date.now()
    };

    console.log('🔔 DataSync event:', event);

    const typeListeners = listenersRef.current.get(type);
    if (typeListeners) {
      typeListeners.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('Error in DataSync listener:', error);
        }
      });
    }

    globalListenersRef.current.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Error in global DataSync listener:', error);
      }
    });

    const customEvent = new CustomEvent('data-sync', { detail: event });
    window.dispatchEvent(customEvent);
  }, []);

  const subscribe = useCallback((type: DataType, callback: (event: DataSyncEvent) => void) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, new Set());
    }

    const listeners = listenersRef.current.get(type)!;
    listeners.add(callback);

    logger.debug(`Subscribed to ${type} changes`);

    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        listenersRef.current.delete(type);
      }
      logger.debug(`Unsubscribed from ${type} changes`);
    };
  }, []);

  const subscribeAll = useCallback((callback: (event: DataSyncEvent) => void) => {
    globalListenersRef.current.add(callback);
    logger.debug('Subscribed to all data changes');

    return () => {
      globalListenersRef.current.delete(callback);
      logger.debug('Unsubscribed from all data changes');
    };
  }, []);

  useEffect(() => {
    return () => {
      listenersRef.current.clear();
      globalListenersRef.current.clear();
    };
  }, []);

  return (
    <DataSyncContext.Provider value={{ notifyDataChange, subscribe, subscribeAll }}>
      {children}
    </DataSyncContext.Provider>
  );
};

export function useDataSync(): DataSyncContextType {
  const context = useContext(DataSyncContext);
  if (!context) {
    throw new Error('useDataSync must be used within DataSyncProvider');
  }
  return context;
}

export const useDataSubscription = (
  type: DataType,
  callback: (event: DataSyncEvent) => void,
  enabled: boolean = true
) => {
  const { subscribe } = useDataSync();

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = subscribe(type, callback);
    return unsubscribe;
  }, [subscribe, type, callback, enabled]);
};
